import React, { useState } from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../components/CustomHook';
import emailjs from 'emailjs-com';
import Notiflix from 'notiflix';
import '../firebase/Firebase';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

const Contact = () => {
  useDocTitle('kesnelDigital - Contact');
  const { t } = useTranslation();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});

  const clearErrors = () => setErrors({});

  const clearInput = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setCompany('');
    setMessage('');
  };

  const sendEmail = () => {
    const userId = process.env.REACT_APP_EMAILJS_USER_ID;
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_CONTACT;

    const templateParams = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone_number: phone,
      company: company,
      message: company.trim() ? `${t('contact.company')} : ${company}\n\n${message}` : message,
    };

    emailjs
      .send(serviceId, templateId, templateParams, userId)
      .then(function () {
        clearInput();
        Notiflix.Report.success(t('contact.success'), t('contact.successMsg'), t('contact.ok'));
      })
      .catch(function () {
        Notiflix.Report.failure(t('contact.error'), t('contact.errorMsg'), t('contact.ok'));
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!firstName.trim()) nextErrors.first_name = t('contact.required');
    if (!lastName.trim()) nextErrors.last_name = t('contact.required');
    if (!email.trim()) nextErrors.email = t('contact.required');
    else if (!/^\S+@\S+\.\S+$/.test(email.trim())) nextErrors.email = t('contact.invalidEmail');
    if (!phone.trim()) nextErrors.phone_number = t('contact.required');
    if (!message.trim()) nextErrors.message = t('contact.required');

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    sendEmail();
  };

  const inputClass =
    'w-full rounded-xl border border-slatex-200 bg-white px-4 py-3 text-ink placeholder-slatex-400 transition focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100';

  const infoItems = [
    {
      title: t('contact.addressTitle'),
      value: t('contact.address'),
      icon: <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0Z" />,
    },
    {
      title: t('contact.callTitle'),
      value: t('contact.phone'),
      href: `tel:${t('contact.phone').replace(/[^+\d]/g, '')}`,
      icon: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.9.36 1.79.68 2.65a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.43-1.43a2 2 0 0 1 2.11-.45c.86.32 1.75.55 2.65.68A2 2 0 0 1 22 16.92Z" />,
    },
    {
      title: t('contact.emailTitle'),
      value: t('contact.email'),
      href: `mailto:${t('contact.email')}`,
      icon: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>,
    },
  ];

  return (
    <>
      <Helmet>
        <meta name="description" content="Get in touch with Kesnel Digital. Let's bring your ideas to life!" />
      </Helmet>
      <NavBar />

      <div className="hero-surface">
        <div className="container-x pt-32 pb-20 lg:pt-40 lg:pb-28">
          <div className="max-w-2xl" data-aos="fade-up">
            <span className="eyebrow">{t('ui.getInTouch')}</span>
            <h1 className="mt-3 text-4xl md:text-5xl font-extrabold text-ink">{t('contact.title')}</h1>
            <p className="mt-4 text-lg text-slatex-600">{t('cta.subtitle')}</p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-3" data-aos="fade-up" data-aos-delay="150">
            {/* Form */}
            <form onSubmit={handleSubmit} noValidate className="lg:col-span-2">
              <div className="rounded-2xl border border-slatex-200 bg-white p-6 md:p-8 shadow-card">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <input name="first_name" className={`${inputClass}${errors.first_name ? ' !border-red-400' : ''}`} type="text" placeholder={t('contact.firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} onKeyUp={clearErrors} />
                    {errors.first_name && <p className="mt-1 text-sm text-red-500">{errors.first_name}</p>}
                  </div>
                  <div>
                    <input name="last_name" className={`${inputClass}${errors.last_name ? ' !border-red-400' : ''}`} type="text" placeholder={t('contact.lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} onKeyUp={clearErrors} />
                    {errors.last_name && <p className="mt-1 text-sm text-red-500">{errors.last_name}</p>}
                  </div>
                  <div>
                    <input name="email" className={`${inputClass}${errors.email ? ' !border-red-400' : ''}`} type="email" placeholder={t('contact.emailPlaceHolder')} value={email} onChange={(e) => setEmail(e.target.value)} onKeyUp={clearErrors} />
                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                  </div>
                  <div>
                    <input name="phone_number" className={`${inputClass}${errors.phone_number ? ' !border-red-400' : ''}`} type="tel" placeholder={t('contact.phonePlaceHolder')} value={phone} onChange={(e) => setPhone(e.target.value)} onKeyUp={clearErrors} />
                    {errors.phone_number && <p className="mt-1 text-sm text-red-500">{errors.phone_number}</p>}
                  </div>
                </div>
                <div className="mt-5">
                  <input name="company" className={inputClass} type="text" placeholder={t('contact.company')} value={company} onChange={(e) => setCompany(e.target.value)} />
                </div>
                <div className="mt-5">
                  <textarea name="message" placeholder={t('contact.messagePlaceHolder')} className={`${inputClass} h-36 resize-none${errors.message ? ' !border-red-400' : ''}`} value={message} onChange={(e) => setMessage(e.target.value)} onKeyUp={clearErrors}></textarea>
                  {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message}</p>}
                </div>
                <button type="submit" id="submitBtn" className="btn-primary mt-6 w-full sm:w-auto">
                  {t('contact.sendButton')}
                </button>
              </div>
            </form>

            {/* Info */}
            <div className="rounded-2xl cta-surface p-8 text-white">
              <h2 className="text-xl font-bold">{t('ui.getInTouch')}</h2>
              <div className="mt-6 space-y-6">
                {infoItems.map((item) => {
                  const content = (
                    <>
                      <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-white/10 text-white transition group-hover:bg-white/20">
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{item.icon}</svg>
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-white">{item.title}</p>
                        <p className="mt-0.5 text-sm text-brand-100/80 transition group-hover:text-white">{item.value}</p>
                      </div>
                    </>
                  );
                  return item.href ? (
                    <a key={item.title} href={item.href} className="group flex gap-4">{content}</a>
                  ) : (
                    <div key={item.title} className="flex gap-4">{content}</div>
                  );
                })}
              </div>

              <div className="mt-8 flex gap-3 border-t border-white/10 pt-6">
                <a href={t('contact.facebook')} target="_blank" rel="noreferrer" aria-label="Facebook" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 transition hover:bg-white hover:text-ink">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="fill-current"><path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z" /></svg>
                </a>
                <a href={t('contact.linkedin')} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 transition hover:bg-white hover:text-ink">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="fill-current"><circle cx="4.983" cy="5.009" r="2.188" /><path d="M9.237 8.855v12.139h3.769v-6.003c0-1.584.298-3.118 2.262-3.118 1.937 0 1.961 1.811 1.961 3.218v5.904H21v-6.657c0-3.27-.704-5.783-4.526-5.783-1.835 0-3.065 1.007-3.568 1.96h-.051v-1.66H9.237zm-6.142 0H6.87v12.139H3.095z" /></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Contact;
